/**
 * # SL-18 — ARRAYRECT → MInsert grid emit (model → DXF decision)
 *
 * Closes a live P1 export data-integrity defect (ТЗ §10 line ~1045 — ARRAYRECT
 * associative object model + rectangular params; §11 line ~1083 — ARRAYRECT in
 * the mandatory full-fidelity set). A NON-associative rectangular array used to
 * ALWAYS serialize as a single `100 AcDbBlockReference` under the bespoke
 * `0 ARRAYRECT` name with a dangling `*ARRAY_<id>` block — an entity AutoCAD /
 * ezdxf read as an unknown `DXFTagStorage` proxy, so the grid was LOST on open.
 *
 * This module makes the model→DXF projection CONDITIONAL:
 *
 *   • NON-associative grid (no ACDBASSOCARRAY graph) whose source `*U…` block
 *     name survived import → emitted as a genuine MINSERT: a `DxfInsertEntity`
 *     (`0 INSERT`) whose column/row grid (70/71/44/45) rides the INSERT writer's
 *     pure COUNT predicate (ezdxf `Insert.export_entity`:
 *     `if column_count>1 or row_count>1: write "AcDbMInsertBlock"`). AutoCAD /
 *     ezdxf then reconstruct the rectangular grid on open. Reusing the INSERT
 *     arm inherits the ALREADY-conformant `0 INSERT` name + single subtype
 *     marker proven by `minsertMarker.conformance.test.ts` (audit 0/0, AC1032).
 *
 *   • ASSOCIATIVE array (an ACDBASSOCARRAY graph is present — `associative` was
 *     flipped true by the second-pass resolver, or `assocHandles` / `assocItems`
 *     / `itemTransforms` are populated) → PRESERVED as a plain `DxfArrayRectEntity`
 *     block reference, BYTE-IDENTICAL to the pre-SL-18 output. A real associative
 *     array's anchor is a PLAIN block reference of the anonymous `*U` block; the
 *     grid lives in the ACDBASSOCARRAY params record, NOT a MINSERT grid on the
 *     entity, so a MInsert upgrade would CORRUPT it (@codex SL-9 P2). The
 *     associativity gate keeps it a block reference BY CONSTRUCTION.
 *
 * The decision is made HERE, at the typed model → DXF boundary, where the
 * resolved `associative` flag is known — NOT at the raw dxf-io serialize
 * boundary (which cannot see the OBJECTS assoc graph). Because the resulting DXF
 * entity TYPE encodes the decision (`INSERT` vs `ARRAYRECT`), a subsequent RAW
 * `serializeDxfDocument(parseDxfDocument(...))` round-trip preserves each without
 * re-deriving associativity: the INSERT arm re-emits `AcDbMInsertBlock` from its
 * own COUNT predicate, the ARRAYRECT arm re-emits `AcDbBlockReference`
 * unconditionally.
 *
 * Source of truth: ТЗ §10/§11; Autodesk DXF R2018 MINSERT group codes (subclass
 * `100 AcDbMInsertBlock`; 70 column count, 71 row count, 44 column spacing,
 * 45 row spacing + standard block-reference 10/20/30 insert, 41/42/43 scale,
 * 50 rotation); ezdxf 1.4.3 `entities/insert.py`. AutoCAD MCP BANNED.
 */
import type {
  DxfArrayRectEntity,
  DxfInsertEntity,
  DxfPoint3D,
} from "@dxf-ai-editor/dxf-io";

import type { ArrayRectEntity, BlockDefinition } from "./model";

/** The shared block-reference base (everything on the transport entity except
 * the `type` discriminant and the per-type `geometry`). Built once by
 * `toDxfEntity` and threaded in so the array arm stays a one-line call. */
type ArrayRectDxfBase = Omit<DxfInsertEntity, "type" | "geometry">;

/**
 * Project a model point to a DXF 3D point, defaulting an absent Z to 0 —
 * identical semantics to `toDxfPoint3D` in dxfBridge.entityConversion.ts. Kept
 * INLINE (not imported) so this module has NO edge back to entityConversion.ts:
 * entityConversion imports THIS module, and the global dep-graph gate forbids a
 * cycle. The projection is an incidental coordinate spread, not a domain truth.
 */
function toArrayDxfPoint3D(point: {
  x: number;
  y: number;
  z?: number;
}): DxfPoint3D {
  return { x: point.x, y: point.y, z: point.z ?? 0 };
}

/** True when `n` is a real OCS normal distinct from the identity (0,0,1). */
function isNonDefaultNormal(n: DxfPoint3D): boolean {
  return !(n.x === 0 && n.y === 0 && n.z === 1);
}

/**
 * Resolve the array's source block to its CANONICAL block-table key — using
 * AutoCAD's CASE-INSENSITIVE block-name semantics — or `undefined` when the
 * block is not defined in the document.
 *
 * Block names fold case-insensitively everywhere the codebase touches them:
 * AutoCAD stores names case-folded on disk and resolves references without
 * regard to case, ezdxf's block table is case-insensitive, and the repo's own
 * reference machinery lowercases before comparing (`referenceAudit.entityRefs`
 * `blockName.toLowerCase()`, `dependencyGraph.definedBlockNames`, the
 * `blockNaming` predicates' `/…/iu` flags). So a `0 ARRAYRECT` whose group-2
 * `sourceBlockName` differs only in CASE from the BLOCK definition key
 * (`gridcell` referencing a block keyed `GridCell`) still points at the SAME
 * block. An exact-key `blocks[name]` guard would report it absent and drop the
 * grid back to the non-associative `0 ARRAYRECT` fallback (grid LOST on open)
 * even though the source block is present — so resolve case-insensitively and
 * the MINSERT upgrade fires whenever the block truly exists.
 *
 * RETURNS THE MATCHED KEY, not a bare boolean: on a case-fold hit the caller
 * must emit the CANONICAL block spelling (the stored `GridCell`), NOT the
 * mismatched source casing (`gridcell`), into the MINSERT group 2. The importer
 * preserves INSERT group 2 as `Block.blockName` and many in-app paths/renderers
 * do EXACT `document.blocks[blockName]` lookups, so emitting the mismatched case
 * would leave the newly-generated MINSERT unresolved inside the app on a
 * save→reopen even though AutoCAD/ezdxf accept it (@codex SL-18 P2).
 *
 * A defined block is REQUIRED for the upgrade (a MINSERT of an undefined block
 * is a dangling INSERT — ezdxf audit ERROR); this resolver never invents one
 * (`undefined` ⇒ the caller keeps the plain-block-reference fallback).
 */
export function resolveSourceBlockName(
  blocks: Record<string, BlockDefinition> | undefined,
  sourceBlockName: string | undefined,
): string | undefined {
  if (blocks === undefined || sourceBlockName === undefined) return undefined;
  // Fast path — exact own-key hit (the overwhelmingly common same-case case):
  // the source name IS the canonical key. `hasOwnProperty` (not
  // `blocks[name] !== undefined`) so a block literally named `constructor` /
  // `toString` can't false-positive off Object.prototype.
  if (Object.prototype.hasOwnProperty.call(blocks, sourceBlockName)) {
    return sourceBlockName;
  }
  // Case-insensitive fallback — AutoCAD folds block names. Return the CANONICAL
  // stored key (`GridCell`), not the mismatched source casing (`gridcell`).
  const target = sourceBlockName.toLowerCase();
  for (const key of Object.keys(blocks)) {
    if (key.toLowerCase() === target) return key;
  }
  return undefined;
}

/**
 * Decide whether a rectangular array should serialize as a MINSERT grid
 * (`0 INSERT` + `AcDbMInsertBlock`) rather than a plain block reference.
 *
 * TRUE requires ALL of — a conservative, associativity-preserving predicate:
 *   1. `associative === false` — the resolved truth (the second-pass resolver
 *      leaves a bare `0 ARRAYRECT` with no assoc graph non-associative; it flips
 *      to `true` when a graph is found). The schema default is `true`, so an
 *      un-resolved / freshly-parsed literal never upgrades by accident.
 *   2. NO ACDBASSOCARRAY graph payload — `assocHandles` absent AND no decoded
 *      `assocItems` AND no evaluated `itemTransforms` AND no
 *      `extensionDictionaryHandle`. Belt-and-suspenders: any graph signal keeps
 *      the entity a block reference even if `associative` were mislabeled. The
 *      ext-dict handle is itself a graph signal — the import join is
 *      `ext-dict → ACAD_ASSOCNETWORK → action body` (dxfBridge.dynamic /
 *      handleResolution), so an ARRAYRECT that carries an extension dictionary
 *      whose ACDBASSOCARRAY graph was NOT resolved (unsupported / partially
 *      parsed: `associative` left false, `assocHandles`/items/transforms empty)
 *      is a would-be associative array whose association is merely unresolved.
 *      Upgrading it to a plain MINSERT would DROP that association graph on
 *      export, so the ext-dict presence keeps it a block reference (@codex
 *      SL-18 P2 — preserve arrays with unresolved extension dictionaries).
 *   3. A real source block name survived import (`sourceBlockName`, the source
 *      group-2 `*U…` / block name) AND that block is DEFINED in the document
 *      (`sourceBlockDefined`). A MINSERT of an undefined block is a dangling
 *      INSERT (ezdxf audit ERROR), so an array whose source block is absent
 *      (e.g. a reducer-built array with no block, or a malformed import with a
 *      dropped BLOCKS section) stays a plain block reference — the fix NEVER
 *      introduces a dangling reference.
 *   4. A real grid — `columnCount > 1 || rowCount > 1`. A 1×1 array is a single
 *      insertion with nothing to reconstruct; ezdxf omits the MInsert marker for
 *      it anyway (pure COUNT predicate), so it stays a plain block reference.
 */
export function arrayRectEmitsAsMInsert(
  entity: ArrayRectEntity,
  sourceBlockDefined: boolean,
): boolean {
  const hasAssocGraph =
    entity.assocHandles !== undefined ||
    (entity.assocItems !== undefined && entity.assocItems.length > 0) ||
    (entity.itemTransforms !== undefined && entity.itemTransforms.length > 0) ||
    // The extension-dictionary handle is the array's join to its ACDBASSOCARRAY
    // OBJECTS graph (ext-dict → ACAD_ASSOCNETWORK → action body). An ext-dict
    // present but the graph unresolved (associative left false, no assoc
    // payload) means the association is merely unresolved — NOT genuinely
    // absent — so preserve it as a block reference rather than dropping the
    // graph via a MINSERT upgrade.
    (typeof entity.extensionDictionaryHandle === "string" &&
      entity.extensionDictionaryHandle.length > 0);
  const sourceBlockName = entity.sourceBlockName;
  const hasSourceBlock =
    typeof sourceBlockName === "string" && sourceBlockName.length > 0;
  const hasGrid = entity.columnCount > 1 || entity.rowCount > 1;
  return (
    entity.associative === false &&
    !hasAssocGraph &&
    hasSourceBlock &&
    sourceBlockDefined &&
    hasGrid
  );
}

/**
 * Build the transport DXF entity for a rectangular array — a `DxfInsertEntity`
 * MINSERT when {@link arrayRectEmitsAsMInsert} holds, otherwise the plain
 * `DxfArrayRectEntity` block reference (pre-SL-18 behaviour, unchanged).
 *
 * The single call site in `toDxfEntity` (dxfBridge.entityConversion.ts) replaces
 * the former inline ARRAYRECT literal, keeping that God-file's growth ~0.
 */
export function buildArrayRectDxfEntity(
  entity: ArrayRectEntity,
  base: ArrayRectDxfBase,
  resolvedSourceBlockName: string | undefined,
): DxfInsertEntity | DxfArrayRectEntity {
  // `resolveSourceBlockName` returned the CANONICAL block-table key (the stored
  // spelling — a case-fold hit resolves `gridcell` → `GridCell`) or `undefined`
  // when the source block is absent. A defined block (resolved !== undefined) is
  // REQUIRED for the MInsert upgrade; the predicate re-checks it via the boolean.
  if (
    resolvedSourceBlockName !== undefined &&
    arrayRectEmitsAsMInsert(entity, true)
  ) {
    // ── MINSERT grid — a genuine block reference of the source block, tiled by
    //    the INSERT writer's COUNT predicate into `AcDbMInsertBlock`. ──
    const geom: DxfInsertEntity["geometry"] = {
      // Emit the CANONICAL block spelling (`resolvedSourceBlockName`), NOT the
      // possibly-mismatched `entity.sourceBlockName` casing — so a save→reopen
      // resolves against the app's exact `document.blocks[blockName]` lookup.
      blockName: resolvedSourceBlockName,
      // S-ARRAYRECT-BASEPOINT-Z — carry the base-point Z (group 30) symmetrically.
      insertionPoint: toArrayDxfPoint3D(entity.basePoint),
    };
    // Grid group codes (70/71/44/45). Elide the count when 1 (single-axis) to
    // match the INSERT arm's compact default-elision; the predicate guarantees
    // at least one axis is > 1 so the MInsert marker still fires.
    if (entity.columnCount !== 1) geom.columnCount = entity.columnCount;
    if (entity.rowCount !== 1) geom.rowCount = entity.rowCount;
    if (entity.columnSpacing) geom.columnSpacing = entity.columnSpacing;
    if (entity.rowSpacing) geom.rowSpacing = entity.rowSpacing;
    // A non-associative array's axisAngle is the source group-50 rotation
    // (DEGREES — read straight off the `0 ARRAYRECT` block reference on import);
    // only the associative path carries a RADIAN AxesAngle, and that path never
    // reaches here. Emit as group 50 exactly like the Block/INSERT arm.
    if (entity.axisAngle) geom.rotation = entity.axisAngle;
    // OCS normal: an INSERT ships its 210/220/230 inside `geometry.extrusion`
    // (the writer reads it there, NOT from `common`). The generic
    // `projectCommonEntityProps` narrow routed the array's non-default extrusion
    // onto `common.extrusion` (ARRAYRECT is not in its Block/INSERT exclusion),
    // so MOVE it onto the INSERT geometry and clear the common copy to avoid a
    // stray common-channel emit / double-emit.
    const ocs = base.common.extrusion;
    if (ocs && isNonDefaultNormal(ocs)) {
      geom.extrusion = { x: ocs.x, y: ocs.y, z: ocs.z };
      base.common.extrusion = undefined;
    }
    return { ...base, type: "INSERT", geometry: geom };
  }

  // ── Plain block reference (associative array, or no reconstructable source
  //    block) — PRESERVED byte-identical to the pre-SL-18 ARRAYRECT arm,
  //    EXCEPT for `rotation` (AW-1 @codex P1 fix, see below). ──
  const geom: DxfArrayRectEntity["geometry"] = {
    blockName: `*ARRAY_${entity.id}`,
    // S-ARRAYRECT-BASEPOINT-Z — `toArrayDxfPoint3D` writes `z ?? 0` so a
    // non-zero elevation survives export.
    insertionPoint: toArrayDxfPoint3D(entity.basePoint),
    columnCount: entity.columnCount,
    rowCount: entity.rowCount,
    columnSpacing: entity.columnSpacing,
    rowSpacing: entity.rowSpacing,
    // AW-1 (@codex P1) — for a NON-associative array (no graph at all)
    // `axisAngle` genuinely IS this entity's own rotation, mirroring the
    // MInsert arm above (pre-existing behaviour, unchanged). For an
    // ASSOCIATIVE array whose graph actually RESOLVED an AxesAngle value
    // at import (`entity.assocParamsSnapshot?.axisAngle !== undefined` —
    // the same by-construction signal AW-0's dirty-diff choke point uses,
    // dxfBridge.arrayAssocExport.ts), `axisAngle` is the GRAPH's "AxesAngle"
    // parameter — the grid axes' orientation WITHIN the array's own local
    // frame (`assocArrayParser.ts`'s composeForLoad doc: the action body's
    // base transform is overwritten from THIS entity's OWN blockTransform,
    // i.e. groups 10/20/30/50 — a SEPARATE, orthogonal world-space
    // placement). Writing the graph angle into group 50 too would rotate
    // the anchor block reference a SECOND time on top of the graph-
    // evaluated item placement — a real geometry bug this fix closes
    // (pre-existing since `axisAngle` was added to the schema, made newly
    // visible by this same PR's units-seam fix, which turned the
    // previously near-invisible radians-as-degrees drift into an obvious
    // full-magnitude double-rotation).
    //
    // A blanket `entity.associative` check (not gated on the snapshot) is
    // TOO BROAD: `associative` is true whenever ANY assoc subgraph is
    // reachable, independent of whether AxesAngle actually decoded — e.g.
    // an expression-driven AxesAngle (`mergeNamedRectValueParams` skips a
    // named value-param with more than one EvalVariant, the formula-chain
    // guard) or a malformed/partial graph. In that case `entity.axisAngle`
    // still holds whatever `dxfBridge.drawingToDxf.ts`'s import set it to —
    // the anchor's OWN raw group-50 rotation, not a graph parameter — and
    // zeroing it would silently drop a real rotation on a byte-stable
    // round-trip of an UNTOUCHED document (@codex P1 round-2, caught by the
    // arrayMinsert.graphUnresolvedRotation regression pair below).
    rotation:
      entity.associative && entity.assocParamsSnapshot?.axisAngle !== undefined
        ? 0
        : entity.axisAngle ?? 0,
  };
  return { ...base, type: "ARRAYRECT", geometry: geom };
}
